import { useState } from "react";
import { PreApprovalCheckIcon, DiagonalArrowIcon } from "src/assets/icons";
import {
  BACKGROUND_LIGHT,
  QUATERNARY_GREY,
  SUCCESS_DARK_BACKGROUND,
} from "src/constants/colors";
import { useApplication } from "./ApplicationContext";
import styled from "@emotion/styled";
import { GhostButton } from "src/components/Buttons";
import { useDealer } from "src/hooks/useDealer";
import { useSafeApiCall } from "src/hooks/api/useSafeApiCall";
import { useApiClient } from "src/hooks/api/useApiClient";
import { API_PREFIX_PATH, APPLICATION_STATUS_KEYS } from "src/constants";
import { downloadDocument } from "src/utils";

const BannerContainer = styled.div`
  display: flex;
  padding: 4px 16px;
  justify-content: space-between;
  align-items: center;
  border-radius: 32px;
  background: ${SUCCESS_DARK_BACKGROUND};
  margin-top: 24px;
  color: ${BACKGROUND_LIGHT};
  font-size: 14px;
  opacity: 1;
  max-height: 100px;
  overflow: hidden;
  pointer-events: auto;
  transition: all 0.3s ease-in-out;
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViewPreApprovalButton = styled(GhostButton)`
  display: flex;
  padding: 8px 10px 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  width: auto;
  color: ${BACKGROUND_LIGHT};
  box-sizing: border-box;
  min-height: 32px;

  &:hover {
    background: ${QUATERNARY_GREY};
    color: ${BACKGROUND_LIGHT};
  }
`;

export default () => {
  const { dealer_id } = useDealer();
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();
  const {
    application: { application_id, primary_lender, application_status, los_id },
  } = useApplication();
  const [loading, setLoading] = useState(false);

  const handleDownloadPreApproval = () => {
    const path = `${API_PREFIX_PATH}/${dealer_id}/application-documents/${application_id}/preapproval`;

    setLoading(true);

    safeApiCall(
      () =>
        downloadDocument(
          fetchFromApi,
          safeApiCall,
          path,
          `pre-approval-${application_id}.pdf`,
          false,
        ).then(() => {
          setLoading(false);
        }),
      {
        toastErrorMsg:
          "Oops! The document is still being prepared. Please try again later.",
        retryFn: () => handleDownloadPreApproval(),
        suppressErrorRedirect: true,
      },
    ).catch(() => {
      setLoading(false);
    });
  };

  if (!primary_lender?.toLowerCase().includes("porchpass")) {
    return null;
  }

  const preApprovedStatuses = [
    APPLICATION_STATUS_KEYS["Pre-approved"],
    APPLICATION_STATUS_KEYS["Clear to close"],
    APPLICATION_STATUS_KEYS.Closed,
    APPLICATION_STATUS_KEYS.Funded,
  ];

  if (
    !(preApprovedStatuses as readonly string[]).includes(
      application_status ?? "",
    )
  ) {
    return null;
  }

  const isDocumentAvailable = Boolean(los_id);

  return (
    <BannerContainer>
      <LeftContainer>
        <PreApprovalCheckIcon />
        <span>Application has been Pre-approved.</span>
      </LeftContainer>
      <ViewPreApprovalButton
        onClick={handleDownloadPreApproval}
        loading={loading}
        disabled={!isDocumentAvailable}
      >
        <span>View pre-approval</span>
        <DiagonalArrowIcon />
      </ViewPreApprovalButton>
    </BannerContainer>
  );
};
